import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useCallback } from 'react';

import { loadSoundCloudLibrary } from '../../interfaces/thirdParty';
import { track } from '@xanadu/ui-shared/interfaces/woopra';

import IconSoundcloud from '../../../images/icons/misc/podcast/icon-soundcloud.svg';
import IconSpotify from '../../../images/icons/misc/podcast/icon-spotify.svg';
import IconApplePodcasts from '../../../images/icons/misc/podcast/icon-apple.svg';
import IconGooglePodcasts from '../../../images/icons/misc/podcast/icon-google.svg';
import IconYoutube from '../../../images/icons/misc/podcast/icon-youtube.svg';
import IconRSS from '../../../images/icons/misc/podcast/icon-rss.svg';

import styles from './Podcast.module.scss';

let trackPosition = 0;
let trackIndex = 0;
// needed to avoid sending tracking event twice since the iframe widget triggers 2 consecutive play events
let playTimes = 0;

const Podcast = ({
  onPlayerStateChange
}) => {
  const iframeElement = useRef();
  const [loading, setLoading] = useState(true);

  const onTrackLoad = useCallback(() => {
    try {
      const widget = window.SC.Widget(iframeElement.current);

      if (trackPosition) {
        widget.skip(trackIndex);
        widget.seekTo(trackPosition - 4000);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Error accessing SC widget', e);
    }
  }, []);

  const onTrackPlay = () => {
    onPlayerStateChange(true);

    playTimes += 1;

    if (playTimes === 1) {
      track('podcast', 'Play Podcast');
    }
  };

  const onTrackPause = useCallback(({ currentPosition }) => {
    trackPosition = currentPosition;
    onPlayerStateChange(false);

    playTimes = 0;

    track('podcast', 'Pause Podcast');

    try {
      const widget = window.SC.Widget(iframeElement.current);

      widget.getCurrentSoundIndex((soundIndex) => {
        trackIndex = soundIndex;
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Error saving SC track index', e);
    }
  }, []);

  useEffect(() => {
    let timeout = setTimeout(() => {
      setLoading(false);

      try {
        loadSoundCloudLibrary();
        const widget = window.SC.Widget(iframeElement.current);

        widget.bind(window.SC.Widget.Events.PLAY, onTrackPlay);
        widget.bind(window.SC.Widget.Events.PAUSE, onTrackPause);
        widget.bind(window.SC.Widget.Events.READY, onTrackLoad);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Error loading SC Library', e);
      }
    }, 400);

    return () => {
      clearTimeout(timeout);
      timeout = undefined;
    };
  }, []);

  const parameters = {
    auto_play: false,
    buying: false,
    liking: false,
    download: false,
    sharing: true,
    show_artwork: false,
    show_comments: false,
    show_playcount: false,
    show_user: false,
    hide_related: false,
    visual: false,
    callback: true,
  };

  const querystring = Object.keys(parameters)
    .map(key => (`${key}=${encodeURIComponent(parameters[key])}`))
    .join('&');

  return (
    <div className={styles.main}>
      {loading && <div className={styles.iframePlaceholder} />}
      {!loading && (
        <iframe
          className={styles.iframe}
          ref={iframeElement}
          title="podcast-player"
          scrolling="no"
          frameBorder="no"
          loading="lazy"
          src={`https://w.soundcloud.com/player/?url=https://soundcloud.com/matchbookbettingpodcast&${querystring}`}
        />
      )}
      <div className={styles.containerSubscriptions}>
        <span className={styles.subscriptionsTitle}>Subscribe</span>
        <div className={styles.subscriptions}>
          <a
            href="https://soundcloud.com/matchbookbettingpodcast"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.subscription}
          >
            <IconSoundcloud /> Soundcloud
          </a>
          <a
            href="https://matchbook.libsyn.com/rss"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.subscription}
          >
            <IconRSS /> RSS
          </a>
          <a
            href="https://open.spotify.com/show/6NwmZVJMpJC1ZPAV5QwOMc"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.subscription}
          >
            <IconSpotify /> Spotify
          </a>
          <a
            href="https://podcasts.google.com/feed/aHR0cHM6Ly9tYXRjaGJvb2subGlic3luLmNvbS9yc3M="
            target="_blank"
            rel="noopener noreferrer"
            className={styles.subscription}
          >
            <IconGooglePodcasts /> Google Podcasts
          </a>
          <a
            href="https://podcasts.apple.com/us/podcast/matchbook-betting-podcast/id1155205594?uo=4"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.subscription}
          >
            <IconApplePodcasts /> Apple Podcasts
          </a>
          <a
            href="https://www.youtube.com/c/TeamMatchbook"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.subscription}
          >
            <IconYoutube /> Youtube
          </a>
        </div>
      </div>
    </div>
  );
};

Podcast.propTypes = {
  onPlayerStateChange: PropTypes.func.isRequired
};

export default Podcast;

